/**
 * =============================================================================
 * SUPABASE CLIENT - CONFIGURAÇÃO PURA (SEM REACT)
 * =============================================================================
 * Este arquivo NUNCA deve importar componentes React, hooks ou contextos.
 * É uma configuração pura de infraestrutura.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Obter variáveis de ambiente
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim() || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() || '';

// Validação simples 
const hasValidConfig = supabaseUrl.length > 0 && supabaseAnonKey.length > 0;

/**
 * Cliente Supabase - Singleton
 * Stored on globalThis to survive HMR reloads and avoid duplicate GoTrueClient warnings.
 */
const _g = globalThis as any;
let supabaseInstance: SupabaseClient | null = _g.__SUPABASE_INSTANCE || null;

function initSupabase(): SupabaseClient | null {
  if (!hasValidConfig) {
    console.warn('⚠️ Supabase não configurado. App funcionará sem recursos de nuvem.');
    return null;
  }

  try {
    const client = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      },
    });
    return client;
  } catch (error) {
    console.error('❌ Erro ao inicializar Supabase:', error);
    return null;
  }
}

// Criar mock client para quando Supabase não está configurado
function createMockClient(): SupabaseClient {
  const mockError = () => Promise.resolve({ data: null, error: { message: 'Supabase não configurado' } });
  
  return {
    from: () => ({
      select: mockError,
      insert: mockError,
      update: mockError,
      delete: mockError,
      upsert: mockError,
    }),
    auth: {
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({ 
        data: { 
          subscription: { unsubscribe: () => {} } 
        } 
      }),
      signInWithPassword: mockError,
      signUp: mockError,
      signOut: () => Promise.resolve({ error: null }),
    },
  } as any as SupabaseClient;
}

// Inicializar na primeira importação (cached on globalThis for HMR)
if (!supabaseInstance) {
  supabaseInstance = initSupabase();
  _g.__SUPABASE_INSTANCE = supabaseInstance;
}

// Exportar cliente (usa mock se não configurado)
export const supabase: SupabaseClient = supabaseInstance || createMockClient();

// ─── Offline-aware auto-refresh ────────────────────────────────
// Prevents endless retry spam when the network is disconnected
if (supabaseInstance && typeof window !== 'undefined') {
  const handleOffline = () => {
    try { supabaseInstance!.auth.stopAutoRefresh(); } catch { /* noop */ }
  };
  const handleOnline = () => {
    try { supabaseInstance!.auth.startAutoRefresh(); } catch { /* noop */ }
  };
  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);

  // If already offline at boot, stop immediately
  if (!navigator.onLine) handleOffline();
}

// Export para testes
export const hasSupabaseConfigured = hasValidConfig;
